import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Wrench, ShieldCheck, Radio, Bell, MessageCircle, Siren } from "lucide-react";
import { SOPORTE, IMG, track, waLink } from "@/data/content";
import { Reveal, SectionTag, TituloLineas } from "@/components/Reveal";
import Desplegable from "@/components/Desplegable";
import { useCertero } from "@/context/CerteroContext";

const ICONS = { wrench: Wrench, shield: ShieldCheck, radio: Radio, bell: Bell };

// Video del taller como fondo del bloque: en bucle, sin sonido, solo cuando
// está en pantalla. Con «reducir movimiento»: la foto fija.
function VideoFondoSoporte() {
  const ref = useRef(null);
  const vidRef = useRef(null);
  const [reduce, setReduce] = useState(false);
  const inView = useInView(ref, { margin: "240px 0px 240px 0px" });

  useEffect(() => setReduce(window.matchMedia("(prefers-reduced-motion: reduce)").matches), []);
  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    if (inView && !reduce) v.play().catch(() => {});
    else v.pause();
  }, [inView, reduce]);

  return (
    <div ref={ref} className="absolute inset-0" aria-hidden="true">
      {reduce ? (
        <img src={IMG.soportePoster} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
      ) : (
        <video
          ref={vidRef}
          data-testid="soporte-video-fondo"
          src="/fotos/soporte-servicio-tecnico.mp4"
          poster={IMG.soportePoster}
          muted
          loop
          playsInline
          preload="none"
          className="h-full w-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-abyss/80" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent" />
    </div>
  );
}

export default function Soporte() {
  const { openLead } = useCertero();
  const [abierto, setAbierto] = useState(null);

  return (
    <section id="soporte" className="scroll-mt-20 border-y border-certero/25 bg-ink px-4 py-14 sm:px-8 sm:py-20 lg:px-12 lg:py-32">
      <div className="mx-auto max-w-[1400px]">
        {/* Bloque principal con video de fondo */}
        <div className="relative overflow-hidden border border-hud">
          <VideoFondoSoporte />
          <div className="relative z-10 flex flex-col gap-8 p-7 sm:p-12 lg:p-16">
            <Reveal>
              <SectionTag>Soporte técnico</SectionTag>
              <TituloLineas
                lineas={["Su equipo nunca deja", "de trabajar. Nosotros tampoco."]}
                className="mt-5 max-w-3xl font-display text-2xl font-bold uppercase leading-tight tracking-tight text-mist sm:text-3xl lg:text-4xl"
              />
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-mist/85 sm:text-base">
                Un departamento exclusivo, independiente de ventas, para que su operación no se
                detenga.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  data-testid="soporte-btn-pedir"
                  onClick={() => { track("pedir_soporte_click", { origen: "seccion_soporte" }); openLead("soporte"); }}
                  className="inline-flex h-12 items-center rounded-full bg-certero px-7 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] active:scale-95"
                >
                  Pedir soporte
                </button>
                <a
                  data-testid="soporte-btn-emergencia"
                  href={waLink("EMERGENCIA EN CAMPO — Hola CERTERO SRL, tengo una emergencia con mi dron y necesito atención inmediata. Le adjunto fotos o video del problema.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("emergencia_whatsapp", { origen: "seccion_soporte" })}
                  className="inline-flex h-12 items-center gap-2 rounded-full border border-red-400/70 px-7 font-display text-xs font-bold uppercase tracking-[0.15em] text-red-300 transition-[background-color,color] duration-300 hover:bg-red-500 hover:text-white"
                >
                  <Siren className="h-4 w-4" /> Emergencia
                </a>
              </div>
            </Reveal>

            {/* Quién lo atiende */}
            <Reveal delay={0.15}>
              <div data-testid="soporte-quien-atende" className="flex flex-col items-start gap-6 border border-hud bg-abyss/85 p-6 backdrop-blur-sm sm:flex-row sm:items-center sm:p-7">
                <img
                  src={IMG.fernandoHurtado}
                  alt="Fernando Hurtado, Encargado del servicio técnico y posventa de CERTERO SRL"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/5] w-28 shrink-0 border border-hud object-cover object-top sm:w-32"
                  data-testid="soporte-foto-fernando"
                />
                <div className="flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-certero">Quién lo atiende</p>
                  <p className="mt-2 font-display text-lg font-bold uppercase tracking-wide text-mist">Fernando Hurtado</p>
                  <p className="mt-1 text-sm leading-relaxed text-smoke">
                    Encargado del servicio técnico y posventa. Atiende su caso de principio a fin: diagnóstico,
                    repuestos originales y pruebas en campo antes de devolverle el equipo.
                  </p>
                </div>
                <a
                  data-testid="soporte-btn-whatsapp-fernando"
                  href={waLink("Hola CERTERO SRL, necesito soporte técnico para mi dron y quiero comunicarme con el departamento técnico.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("soporte_fernando_whatsapp", {})}
                  className="inline-flex h-12 items-center gap-2 self-start rounded-full border border-certero px-6 font-display text-xs font-bold uppercase tracking-[0.15em] text-certero transition-[background-color,color] duration-300 hover:bg-certero hover:text-ink"
                >
                  <MessageCircle className="h-4 w-4" /> Escribir por WhatsApp
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Desplegable: cómo lo respaldamos */}
        <Reveal delay={0.1} className="mt-10">
          <Desplegable
            id="respaldo"
            titulo="Cómo lo respaldamos"
            icono={ShieldCheck}
            compacto
            abierto={abierto === "respaldo"}
            onToggle={(id) => setAbierto((a) => (a === id ? null : id))}
            testid="soporte-desplegable-respaldo"
          >
            <div className="grid flex-1 grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {SOPORTE.map((s, i) => {
                const Icon = ICONS[s.icon];
                return (
                  <div
                    key={s.id}
                    data-testid={`soporte-card-${s.id}`}
                    className="group relative flex flex-col border border-hud bg-abyss p-6"
                  >
                    <Icon className="h-5 w-5 text-certero" strokeWidth={1.5} />
                    <h3 className="mt-5 font-display text-base font-bold uppercase tracking-wide text-mist">{s.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-smoke">{s.d}</p>
                  </div>
                );
              })}
            </div>
          </Desplegable>
        </Reveal>
      </div>
    </section>
  );
}
